import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Sparkles, Loader2, Plus, Check, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRIO = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };
const KIND = { clarification: 'Clarification', inclusion: 'Inclusion', exclusion: 'Exclusion', scope: 'Scope' };

/** "Analyze with AI" — suggestions to add, rewrites to apply, and risks to check. */
export const QuoteAIReview = ({ quoteId, document, review, onReview, onAdd, onRewrite }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState(new Set());

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/quote-doc/${quoteId}/analyze`, { document }, { timeout: 180000 });
      onReview(r.data);
      setDone(new Set());
      setOpen(true);
      toast.success('AI review complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI review failed');
    } finally { setBusy(false); }
  };

  const mark = (id) => setDone((s) => new Set([...s, id]));
  const additions = (review?.additions || []).filter((a) => !done.has(a.id));
  const rewrites = (review?.rewrites || []).filter((r) => !done.has(r.rid));

  return (
    <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-white" data-testid="ai-review-panel">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-violet-600" />
          <div>
            <h4 className="font-semibold text-slate-900">Analyze with AI</h4>
            <p className="text-xs text-slate-500">Scans scope, pricing, inclusions/exclusions and clarifications for gaps and loose wording.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {review && <button onClick={() => setOpen((o) => !o)} className="p-1 rounded hover:bg-violet-100 text-slate-500" data-testid="ai-review-toggle">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>}
          <Button onClick={run} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="ai-analyze-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reviewing…</> : <><Sparkles className="h-4 w-4 mr-2" />{review ? 'Re-analyze' : 'Analyze with AI'}</>}
          </Button>
        </div>
      </div>
      {review && open && (
        <div className="px-4 pb-4 space-y-4 text-sm">
          <p className="text-slate-700" data-testid="ai-summary">{review.summary}</p>
          {review.risks?.length > 0 && (
            <div>
              <p className="font-semibold text-slate-800 mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-amber-600" />Things to check</p>
              <ul className="space-y-1">
                {review.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`ai-risk-${i}`}>
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded mt-0.5 ${PRIO[r.severity] || PRIO.low}`}>{r.severity}</span>
                    <span className="text-slate-700">{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {additions.length > 0 && (
            <div>
              <p className="font-semibold text-slate-800 mb-1">Suggested additions ({additions.length})</p>
              <ul className="space-y-2">
                {additions.map((a) => (
                  <li key={a.id} className="rounded border border-slate-200 bg-white p-2.5" data-testid={`ai-add-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{KIND[a.kind]}</span>
                          <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${PRIO[a.priority] || PRIO.low}`}>{a.priority}</span>
                          {a.library_id && <span className="text-[10px] uppercase font-semibold text-slate-500">from library</span>}
                        </div>
                        <p className="text-slate-900">{a.text}</p>
                        {a.reason && <p className="text-xs text-slate-500 mt-1">{a.reason}</p>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8" onClick={() => { onAdd(a); mark(a.id); }} data-testid={`ai-add-accept-${a.id}`}><Plus className="h-4 w-4 mr-1" />Add</Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => mark(a.id)} data-testid={`ai-add-ignore-${a.id}`}>Ignore</Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {rewrites.length > 0 && (
            <div>
              <p className="font-semibold text-slate-800 mb-1">Tighter wording ({rewrites.length})</p>
              <ul className="space-y-2">
                {rewrites.map((r) => (
                  <li key={r.rid} className="rounded border border-slate-200 bg-white p-2.5" data-testid={`ai-rewrite-${r.rid}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-1">
                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{KIND[r.kind] || r.kind}</span>
                        <p className="text-slate-500 line-through text-xs">{r.original}</p>
                        <p className="text-slate-900">{r.suggested}</p>
                        {r.reason && <p className="text-xs text-slate-500">{r.reason}</p>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8" onClick={() => { onRewrite(r); mark(r.rid); }} data-testid={`ai-rewrite-accept-${r.rid}`}><Check className="h-4 w-4 mr-1" />Apply</Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => mark(r.rid)} data-testid={`ai-rewrite-ignore-${r.rid}`}>Ignore</Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {additions.length === 0 && rewrites.length === 0 && <p className="text-slate-500 italic">All suggestions handled.</p>}
          <p className="text-[11px] text-slate-500">Reviewed {new Date(review.reviewed_at).toLocaleString()} by {review.reviewed_by} · {review.model}</p>
        </div>
      )}
    </div>
  );
};
